import { useEffect, useMemo, useRef, useState } from 'react'

import { Text } from '@hhgtech/hhg-components/mantine'
import { AspectRatio, Box, BoxProps, Flex, rem, Stack } from '@mantine/core'
import Odometer from 'odometer'
import { useInView } from 'react-intersection-observer'

import { CustomizeBlockItem } from 'interfaces/types'

import { getOdometerFormat } from './helper'
import useStyles from './index.styles'

import 'odometer/themes/odometer-theme-default.css'

type Props = { size?: 'sm' | 'md' } & CustomizeBlockItem & BoxProps
export const Item = ({
  size = 'sm',
  icon,
  backgroundColor,
  header,
  content,
  description,
  styles,
  classNames,
  className,
  ...restProps
}: Props) => {
  const { classes, cx } = useStyles(
    { backgroundColor, icon },
    {
      name: 'CustomizeBlock__Item',
      styles,
      classNames,
    },
  )
  const { value: contentVal, color: contentColor } = content
  const { value: descriptionVal, color: descriptionColor } = description
  const { value: headerVal, color: headerColor } = header
  const elRef = useRef(null)
  const odRef = useRef<typeof Odometer | null>(null)
  const { ref: containerRef, inView } = useInView({
    rootMargin: '56px 0px 0px 0px',
  })
  const formatedValue = useMemo(() => Number(contentVal), [])
  const [ready, setReady] = useState(false)
  const [animationPlayed, setAnimationPlayed] = useState(false)

  useEffect(() => {
    if (isNaN(formatedValue)) return
    import('odometer').then(({ default: Odometer }) => {
      odRef.current = new Odometer({
        el: elRef.current,
        value: 0,
        format: getOdometerFormat(contentVal),
        theme: 'default',
        duration: 800,
      })
      setReady(true)
    })
  }, [])

  useEffect(() => {
    if (!inView || animationPlayed || !ready) return

    const timer = setTimeout(() => {
      if (inView && elRef.current && typeof window !== 'undefined') {
        odRef.current.update(formatedValue)
        setAnimationPlayed(true)
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [ready, inView, animationPlayed, formatedValue])

  return (
    <Box
      className={cx(className, classes.root)}
      {...restProps}
      ref={containerRef}
    >
      <Flex
        gap={4}
        justify={'space-between'}
        align={'center'}
        className={classes.inner}
      >
        {icon && (
          <AspectRatio ratio={1} className={classes.icon}>
            <img loading="lazy" src={icon} alt={headerVal} />
          </AspectRatio>
        )}
        <Stack
          spacing={18}
          sx={{ textAlign: 'right' }}
          className={classes.info}
        >
          {headerVal && (
            <Text
              size={size === 'md' ? 'p2' : 'p3'}
              color={headerColor}
              variant="secondary"
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  fontSize: rem(15),
                },
              })}
            >
              {headerVal}
            </Text>
          )}
          {(contentVal || descriptionVal) && (
            <Stack spacing={4} className={classes.info}>
              <Text
                size={size === 'md' ? 'h3' : 'h4'}
                weight="bold"
                variant="primary"
                ref={elRef}
                color={contentColor}
                sx={(theme) => ({
                  [theme.fn.smallerThan('sm')]: {
                    fontSize: rem(26),
                  },
                })}
              >
                {contentVal}
              </Text>
              <Text
                size={size === 'md' ? 'c1' : 'c2'}
                color={descriptionColor}
                variant="tertiary"
              >
                {descriptionVal}
              </Text>
            </Stack>
          )}
        </Stack>
      </Flex>
    </Box>
  )
}
